'use client';

import { HomeDataVilla } from '@/actions/home';
import { AnimatedLink } from '@/components/AnimatedLink';
import VillaCard from '@/components/HomeSections/HomeFeaturedVillas/VillaCard';
import { IHomeDictionary } from '@/dictionaries/pages/home/types';
import { ICurrencyDictonary } from '@/dictionaries/utils/currency/types';
import { IRegionZoneDictonary } from '@/dictionaries/utils/region-zone/types';
import { useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Slider, { Settings } from 'react-slick';

type HomeFeaturedVillasProps = {
  villas: HomeDataVilla[];
  lang: Locale;
  dict: IHomeDictionary;
  regionZoneDict: IRegionZoneDictonary;
  currencyDict: ICurrencyDictonary;
};

const settings: Settings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  dotsClass: 'carousel-dots',
};

export function HomeFeaturedVillas({
  villas,
  lang,
  dict,
  regionZoneDict,
  currencyDict,
}: HomeFeaturedVillasProps) {
  const [sliderRef, setSliderRef] = useState<Slider | null>(null);

  const [firstVilla, ...rest] = villas;

  if (!firstVilla) {
    return null;
  }

  return (
    <section className="pt-32 max-w-default mx-auto">
      <div className="text-center">
        <h2
          className="text-4xl font-title mb-2"
          dangerouslySetInnerHTML={{
            __html: dict.highlightedVillas.title,
          }}
        />
        <p className="text-lg mt-5 mb-12 text-gray-850 max-w-[90%] mx-auto">
          {dict.highlightedVillas.description}
        </p>

        <div className="flex flex-row items-center justify-center gap-4 desktop:gap-8 mb-8 desktop:mb-12">
          <AnimatedLink
            href={dict.highlightedVillas.cta.sicily.href}
            target="_blank"
            className="w-fit py-2 px-4 desktop:py-5 min-h-14 mt-0 desktop:px-[40px] desktop:w-fit flex justify-center items-center text-center flex-1 desktop:flex-none">
            {dict.highlightedVillas.cta.sicily.title}
          </AnimatedLink>

          <AnimatedLink
            href={dict.highlightedVillas.cta.puglia.href}
            target="_blank"
            className="w-fit py-2 px-4 desktop:py-5 min-h-14 mt-0 desktop:px-[40px] desktop:w-fit flex justify-center items-center text-center flex-1 desktop:flex-none">
            {dict.highlightedVillas.cta.puglia.title}
          </AnimatedLink>
        </div>
      </div>

      <div className="flex flex-col gap-10 mb-12">
        <VillaCard
          villa={firstVilla}
          lang={lang}
          dict={regionZoneDict}
          currencyDict={currencyDict}
          isFirst
        />

        <div className="relative desktop:hidden">
          <button
            id="home-carousel-previous-button"
            aria-label="Carousel previous button"
            className="rounded-full z-10 text-zinc-400 flex justify-center
        group items-center bg-white shadow-lg overflow-hidden absolute top-1/2 left-0  translate-y-[-50%] mt-[-50px] w-11 h-[44px]"
            type="button"
            onClick={sliderRef?.slickPrev}>
            <FiChevronLeft className="w-6 h-6 stroke-slate-600 opacity-30 group-hover:opacity-20 transition" />
          </button>

          <Slider {...settings} ref={setSliderRef}>
            {rest.map((villa) => (
              <div className="px-2" key={villa.slug}>
                <VillaCard
                  villa={villa}
                  lang={lang}
                  dict={regionZoneDict}
                  currencyDict={currencyDict}
                />
              </div>
            ))}
          </Slider>

          <button
            id="home-carousel-next-button"
            aria-label="Carousel next button"
            className="rounded-full z-10 text-zinc-400 flex justify-center
        group items-center bg-white shadow-lg overflow-hidden absolute top-1/2 right-0 translate-y-[-50%] mt-[-50px] w-11 h-[44px]"
            type="button"
            onClick={sliderRef?.slickNext}>
            <FiChevronRight className="w-6 h-6 stroke-slate-600 opacity-30 group-hover:opacity-20 transition" />
          </button>
        </div>

        <div className="desktop:flex flex-col desktop:flex-row gap-8 hidden">
          {rest.map((villa) => (
            <VillaCard
              key={villa.slug}
              villa={villa}
              lang={lang}
              dict={regionZoneDict}
              currencyDict={currencyDict}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
